import React, { createContext, useContext, useState } from 'react';
import { Check, Send } from 'lucide-react';

import { DataState } from '../../context/DataProvider';

// The agent asks follow-up questions as a fenced ```questions block (see the
// backend skill skills/dashboard-builder/SKILL.md):
//   [{ "id": "project", "text": "Which project?", "options": ["OD"], "multi": false }]
// This renders it as a card; submitting sends the answers as the user's next
// chat message, so the agent reads them like any reply.

const MAX_QUESTIONS = 4;

// False for a card in an older message (the chat moved on) or while a reply
// streams: the card is read-only, so a reloaded chat cannot resend old answers.
export const QuestionsActive = createContext(true);

/** The questions in a block, or null if it is not a valid questions list. */
export const parseQuestions = (source) => {
  let data;
  try {
    data = JSON.parse(source);
  } catch {
    return null;
  }
  if (!Array.isArray(data) || !data.length) return null;
  const questions = data.slice(0, MAX_QUESTIONS).map((q, i) => ({
    id: String(q?.id ?? i),
    text: typeof q?.text === 'string' ? q.text.trim() : '',
    options: Array.isArray(q?.options)
      ? q.options.filter((o) => typeof o === 'string' && o.trim())
      : [],
    multi: Boolean(q?.multi),
  }));
  return questions.every((q) => q.text) ? questions : null;
};

/** "Question: answer" lines, skipping unanswered ones. */
export const formatAnswers = (questions, answers) =>
  questions
    .map((q) => {
      const a = answers[q.id] || {};
      const parts = [...(a.picked || []), (a.other || '').trim()].filter(
        Boolean,
      );
      return parts.length ? `${q.text} ${parts.join(', ')}` : null;
    })
    .filter(Boolean)
    .join('\n');

const QuestionsCard = ({ questions }) => {
  const { handleStreamSend } = DataState();
  const [answers, setAnswers] = useState({});
  const [sent, setSent] = useState(false);
  const active = useContext(QuestionsActive);
  const closed = sent || !active;

  const toggle = (q, option) =>
    setAnswers((prev) => {
      const picked = prev[q.id]?.picked || [];
      const has = picked.includes(option);
      let next;
      if (q.multi)
        next = has ? picked.filter((o) => o !== option) : [...picked, option];
      else next = has ? [] : [option];
      return { ...prev, [q.id]: { ...prev[q.id], picked: next } };
    });

  const setOther = (q, value) =>
    setAnswers((prev) => ({
      ...prev,
      [q.id]: { ...prev[q.id], other: value },
    }));

  const message = formatAnswers(questions, answers);

  const submit = () => {
    if (!message || closed || !handleStreamSend) return;
    setSent(true);
    handleStreamSend(null, message);
  };

  return (
    <div className="not-prose my-3 flex flex-col gap-4 rounded-xl border border-zinc-700 bg-zinc-900 p-4">
      {questions.map((q) => {
        const picked = answers[q.id]?.picked || [];
        return (
          <fieldset
            key={q.id}
            disabled={closed}
            className="flex flex-col gap-2"
          >
            <legend className="mb-2 text-sm font-medium text-zinc-100">
              {q.text}
              {q.multi && (
                <span className="ml-2 text-xs font-normal text-zinc-500">
                  pick any
                </span>
              )}
            </legend>
            {q.options.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {q.options.map((option) => {
                  const on = picked.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(q, option)}
                      className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-60 ${
                        on
                          ? 'border-[#5652CF] bg-[#5652CF]/25 text-zinc-100'
                          : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                      }`}
                    >
                      {on && <Check size={12} aria-hidden="true" />}
                      {option}
                    </button>
                  );
                })}
              </div>
            )}
            <input
              type="text"
              aria-label={`Other answer: ${q.text}`}
              placeholder={
                q.options.length ? 'Or type your own' : 'Your answer'
              }
              value={answers[q.id]?.other || ''}
              onChange={(e) => setOther(q, e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-[#5652CF] focus:outline-none"
            />
          </fieldset>
        );
      })}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={!message || closed}
          className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          <Send size={12} aria-hidden="true" />
          {sent ? 'Sent' : closed ? 'Closed' : 'Send answers'}
        </button>
      </div>
    </div>
  );
};

export default QuestionsCard;
